import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import TextInput from '../../../components/ui/TextInput.jsx';
import GenericSelect from '../../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import Button from '../../../components/ui/Button';
import { searchDepartments } from '../config';

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 640px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const DialogTitle = styled.h3`
  margin: 0 0 16px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ReadonlyValue = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 8px 0;
`;

// Skorlama Plan 4, Task 4 — OrgGoal create/edit formu. OwnerType/PeriodId
// (goal varsa ownerType prop'undan, yoksa OrgGoalsPage'in tıklanan butonundan
// gelir) her ikisi de FORMDA DÜZENLENEMEZ — backend Update'te bu iki alanı
// değişmez kabul ediyor (OrgGoalDataAccess.Update, task-1-report.md #7).
// DepartmentId de aynı nedenle yalnız YENİ departman hedefinde seçilebilir —
// mevcut hedefte salt-okunur metin olarak gösterilir.
export default function OrgGoalFormModal({
  open, ownerType, goal, periodId, parentCandidates, onCreate, onUpdate, onClose, onSaved,
}) {
  const { t } = useTranslation();
  const isNew = !goal;

  const [title, setTitle] = useState(goal?.title || '');
  const [description, setDescription] = useState(goal?.description || '');
  const [departmentId, setDepartmentId] = useState(goal?.departmentId || null);
  const [parentOrgGoalId, setParentOrgGoalId] = useState(goal?.parentOrgGoalId || null);
  const [progressMode, setProgressMode] = useState(goal?.progressMode || 'manual');
  const [manualProgressPct, setManualProgressPct] = useState(
    goal?.manualProgressPct != null ? String(goal.manualProgressPct) : '',
  );
  const [saving, setSaving] = useState(false);

  const progressModeOptions = useMemo(() => ([
    { id: 'manual', name: t('performance.orgGoals.progressMode.manual') },
    { id: 'rollup', name: t('performance.orgGoals.progressMode.rollup') },
  ]), [t]);

  // parentCandidates: OrgGoalsPage'in flattenTree(tree) + getAllowedParentTypes
  // ile ÖNCEDEN filtrelediği aday listesi (ownerType'a uygun tip + arşivsiz +
  // kendisi hariç) — ayrı bir API çağrısı gerekmez, ağaç zaten sayfada yüklü.
  const parentOptions = useMemo(() => (parentCandidates || []).map((c) => ({
    id: c.id,
    name: `${c.title} (${t(`performance.orgGoals.ownerType.${c.ownerType}`, { defaultValue: c.ownerType })})`,
  })), [parentCandidates, t]);

  const modalTitle = useMemo(() => {
    if (!isNew) return t('performance.orgGoals.form.editTitle');
    if (ownerType === 'department') return t('performance.orgGoals.form.newDepartmentTitle');
    if (ownerType === 'team') return t('performance.orgGoals.form.newTeamTitle');
    return t('performance.orgGoals.form.newCompanyTitle');
  }, [isNew, ownerType, t]);

  const showDepartmentField = ownerType === 'department';
  const showParentField = ownerType !== 'company';
  const canEditDepartment = isNew && showDepartmentField;

  const buildPayload = useCallback(() => ({
    periodId,
    ownerType,
    departmentId: showDepartmentField ? (isNew ? departmentId : goal.departmentId) : undefined,
    title: title.trim(),
    description: description.trim() || undefined,
    parentOrgGoalId: showParentField ? (parentOrgGoalId || undefined) : undefined,
    progressMode,
    manualProgressPct: progressMode === 'manual' && manualProgressPct !== '' ? Number(manualProgressPct) : undefined,
  }), [
    periodId, ownerType, showDepartmentField, isNew, departmentId, goal, title, description,
    showParentField, parentOrgGoalId, progressMode, manualProgressPct,
  ]);

  const canSave = title.trim().length > 0 && (!canEditDepartment || !!departmentId);

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    const payload = buildPayload();
    const ok = isNew ? await onCreate(ownerType, payload) : await onUpdate(goal.id, ownerType, payload);
    setSaving(false);
    if (ok) onSaved();
  }, [canSave, buildPayload, isNew, onCreate, onUpdate, ownerType, goal, onSaved]);

  if (!open) return null;

  return (
    <Modal>
      <ModalCard>
        <DialogTitle>{modalTitle}</DialogTitle>

        <FieldGroup>
          <TextInput
            label={t('performance.orgGoals.form.title')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </FieldGroup>

        <FieldGroup>
          <TextInput
            label={t('performance.orgGoals.form.description')}
            type="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FieldGroup>

        {showDepartmentField && (
          <FieldGroup>
            {canEditDepartment ? (
              <AsyncGenericSelect
                label={t('performance.orgGoals.form.department')}
                value={departmentId}
                onChange={setDepartmentId}
                onSearch={searchDepartments}
                required
              />
            ) : (
              <ReadonlyValue>
                {t('performance.orgGoals.form.department')}: {goal?.departmentName}
              </ReadonlyValue>
            )}
          </FieldGroup>
        )}

        {showParentField && (
          <FieldGroup>
            <GenericSelect
              label={t('performance.orgGoals.form.parentGoal')}
              placeholder={t('performance.orgGoals.form.noParent')}
              value={parentOrgGoalId}
              onChange={setParentOrgGoalId}
              options={parentOptions}
              idKey="id"
              labelKey="name"
            />
          </FieldGroup>
        )}

        <FieldGroup>
          <GenericSelect
            label={t('performance.orgGoals.form.progressMode')}
            value={progressMode}
            onChange={setProgressMode}
            options={progressModeOptions}
            idKey="id"
            labelKey="name"
          />
        </FieldGroup>

        {progressMode === 'manual' && (
          <FieldGroup>
            <TextInput
              data-role="manual-progress-pct"
              label={t('performance.orgGoals.form.manualProgressPct')}
              type="number"
              min={0}
              max={100}
              value={manualProgressPct}
              onChange={(e) => setManualProgressPct(e.target.value)}
            />
          </FieldGroup>
        )}

        <Actions>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} disabled={!canSave || saving} loading={saving}>
            {t('common.save')}
          </Button>
        </Actions>
      </ModalCard>
    </Modal>
  );
}
